import React from 'react';
import { Dropdown } from 'antd';
import { UserOutlined, AppstoreOutlined, SettingOutlined, LogoutOutlined } from '@ant-design/icons';
import MobileNavigationMenu from 'components/MobileMenu/MobileNavigationMenu';
import { useMutation } from '@apollo/client';
import GQLHOC from 'hocs/GQLHOC';
import LOGOUT from 'pages/Auth/_qql/LOGOUT';
import AUTHORIZE_AS from 'pages/Auth/_qql/AUTHORIZE_AS';
import LogoutIcon from 'components/IconAntDesign/LogoutIcon';
import SettingsIcon from 'components/IconAntDesign/SettingsIcon';
import ControlCabinetIcon from 'components/IconAntDesign/ControlCabinetIcon';
import MyProfileIcon from 'components/IconAntDesign/MyProfileIcon';
import { SPORT_URL_PART } from 'pages/Sport/_constants/SPORT_URLS';
import { urlRootPath } from 'helpers/urlPath';
import { USER_PAGE_URL_PART } from 'pages/PersonalSettingsPage/URL';
import { Link, NavLink, matchPath, useLocation } from 'react-router-dom';
import cookies from 'browser-cookies';
import { useTranslation } from 'react-i18next'
import { deleteAuthData } from '../../../../../utils/handleAuthData';
import _Page_Header_User_ViewAs from './_Page_Header_User_ViewAs';
import _Page_Header_User_Name from './_Page_Header_User_Name';
import sportContextConnector from 'pages/Sport/_context/sportContextConnector';


// Route-aware selection is mobile-only; legacy widgets can render without a router.
const MobileAccountMenu = ({ items, paths }) => {
  const { pathname } = useLocation()
  const selected_keys = Object.keys(paths).filter(key =>
    matchPath(pathname, { path: paths[key], exact: key === 'my_profile' })
  )
  return (
    <MobileNavigationMenu
      data-testid="catalog-account-menu"
      selectedKeys={selected_keys}
      items={items.filter(item => !['view_as', 'divider'].includes(item.key))}
    />
  )
}

const withSport = sportContextConnector(['sport_id', 'base_url', 'is_sport_context'])
const withGQL = GQLHOC(() => {
  return {
    logout: useMutation(LOGOUT),
    authAs: useMutation(AUTHORIZE_AS),
  };
});
const _Page_Header_User_Menu = withSport(withGQL(({ user, self, authAs, logout, react_widget, base_url, is_sport_context, mobile, section, before_action }) => {
  const { t } = useTranslation()
  const self_is_admin = self?.roles?.is_admin;
  const isViewAs = user.auser_id !== self.auser_id;

  const base_sport_url = is_sport_context ? base_url : urlRootPath(SPORT_URL_PART, user.default_sport_key);
  const menu_paths = {
    my_profile: urlRootPath(SPORT_URL_PART, USER_PAGE_URL_PART, user.auser_id),
    lk: urlRootPath(base_sport_url, 'admin'),
    settings: urlRootPath(SPORT_URL_PART, USER_PAGE_URL_PART, user.auser_id, 'edit'),
  }


  const menu_auto_style = {
    height: 'auto',
    lineHeight: 'normal',
  }

  const menu_item_with_icon_style = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
  };

  const logout_action = async () => {
    // если пользователь просматривает чужой профиль, то кнопка выхода выводит его из-под чужого пользователя
    if (isViewAs) {
      await authAs({
        variables: { auth_as_user: '0' },
      });

      window.location.reload();
      return;
    }

    // если пользователь просматривает свой профиль, то кнопка выхода выходит из системы
    const token = cookies.get('auth.token');
    const res = await logout({
      variables: { token },
    });

    if (res.data.logout === true) {
      deleteAuthData();
    }

    window.location.reload();
  };

  const logout_click = () => {
    if (before_action?.(logout_action)) return
    return logout_action()
  }

  // TODO: после обновления react router dom просто использоваться <Link> с reloadDocument
  const _get_link = (url, title) => {
    if (!react_widget) {
      return mobile
        ? <NavLink to={url} exact={url === menu_paths.my_profile}>{title}</NavLink>
        : <Link to={url}>{title}</Link>
    }

    return <a href={url}>{title}</a>
  }

  const menu_items = [
    ...(self_is_admin ? [
      {
        key: 'view_as',
        label: <_Page_Header_User_ViewAs user={user} self={self} authAs={authAs} />,
        // prevent the menu item from being closed on click, otherwise it works all the same
        disabled: true,
        style: menu_auto_style,
      },
      {
        key: 'divider',
        type: 'divider',
      }
    ] : []),
    {
      key: 'my_profile',
      icon: mobile ? <UserOutlined /> : <MyProfileIcon />,
      label: _get_link(menu_paths.my_profile, t('My profile')),
      style: menu_item_with_icon_style,
    },
    {
      key: 'lk',
      icon: mobile ? <AppstoreOutlined /> : <ControlCabinetIcon />,
      label: _get_link(menu_paths.lk, t('Personal account')),
      style: menu_item_with_icon_style,
    },
    {
      key: 'settings',
      icon: mobile ? <SettingOutlined /> : <SettingsIcon />,
      label: _get_link(menu_paths.settings, t('Profile settings')),
      style: menu_item_with_icon_style,
    },
    {
      key: 'logout',
      icon: mobile ? <LogoutOutlined /> : <LogoutIcon />,
      label: t('Log out'),
      onClick: logout_click,
      style: menu_item_with_icon_style,
    },
  ];

  if (mobile && section === 'admin') return self_is_admin ? (
    <div className="mb-xs px-ss py-xs" data-testid="catalog-admin-view-as">
      <_Page_Header_User_ViewAs user={user} self={self} authAs={authAs} before_action={before_action} mobile />
    </div>
  ) : null

  if (mobile) return (
    <MobileAccountMenu items={menu_items} paths={menu_paths} />
  )

  return (
    <Dropdown
      placement="bottomRight"
      arrow={false}
      menu={{
        items: menu_items,
        selectable: false,
        triggerSubMenuAction: 'click',
      }}
      trigger={['click']}
    >
      <a onClick={(e) => e.preventDefault()} style={{display: "block"}}>
        <_Page_Header_User_Name user={user} self={self} />
      </a>
    </Dropdown>
  );
}));

export default _Page_Header_User_Menu;
